/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiSelect } from '@elastic/eui'

import type { StackDeployment } from '@modules/ui-types'

import JqExpression from './JqExpression'
import RegexExpression from './RegexExpression'

import type { ConsoleRequestState } from '../../../reducers/apiConsole'
import type { WrappedComponentProps } from 'react-intl'
import type { FunctionComponent } from 'react'

export interface Props {
  request: ConsoleRequestState
  onChange: (request: ConsoleRequestState, deployment: StackDeployment) => void
  deployment: StackDeployment
}

const messages = defineMessages({
  filterResponseBy: {
    id: 'api-console-request.filter-by',
    defaultMessage: 'Filter response by',
  },
  regularExpression: {
    id: 'api-console-request.filter-by.regular-expression',
    defaultMessage: 'Regular expression',
  },
  jqExpression: {
    id: 'api-console-request.filter-by.jq-expression',
    defaultMessage: 'JQ expression',
  },
})

const AdvancedFields: FunctionComponent<Props & WrappedComponentProps> = ({
  intl: { formatMessage },
  request,
  onChange,
  deployment,
}) => (
  <EuiFlexGroup>
    <EuiFlexItem grow={false}>
      <EuiFormRow label={formatMessage(messages.filterResponseBy)}>
        <EuiSelect
          value={request.filterBy || 'regex'}
          onChange={(e) =>
            onChange({ ...request, filterBy: e.target.value as 'regex' | 'jq' }, deployment)
          }
          options={[
            {
              value: 'regex',
              text: formatMessage(messages.regularExpression),
            },
            {
              value: 'jq',
              text: formatMessage(messages.jqExpression),
            },
          ]}
        />
      </EuiFormRow>
    </EuiFlexItem>

    {request.filterBy === 'regex' && (
      <RegexExpression onChange={onChange} request={request} deployment={deployment} />
    )}

    {request.filterBy === 'jq' && (
      <JqExpression onChange={onChange} request={request} deployment={deployment} />
    )}
  </EuiFlexGroup>
)

export default injectIntl(AdvancedFields)
